import { clsx } from 'clsx'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import type { SetupStep } from '@/pages/wordpress-setup/setup-guide-steps'
import { TestConnection } from '@/pages/wordpress-setup/test-connection'

interface StepRowProps {
  step: SetupStep
  isDone: boolean
  isExpanded: boolean
  onToggle: () => void
  shortcodeConfirmed: boolean
  onShortcodeToggle: () => void
  audienceId: string
  hasAudience: boolean
  /** Custom inline content rendered inside the expanded area */
  renderContent?: React.ReactNode
  onTestPassed?: () => void
}

export function StepRow({
  step,
  isDone,
  isExpanded,
  onToggle,
  shortcodeConfirmed,
  onShortcodeToggle,
  audienceId,
  hasAudience,
  renderContent,
  onTestPassed,
}: StepRowProps): React.JSX.Element {
  const Icon = step.icon
  const hasDetails = step.instructions.length > 0 || step.manual || step.id === 6 || !!renderContent

  return (
    <div
      className={clsx(
        'rounded-lg border transition-colors',
        isDone ? 'border-success/30 bg-green-50/50' : 'border-surface-3 bg-surface-0',
      )}
    >
      <button
        type="button"
        onClick={hasDetails ? onToggle : undefined}
        className={clsx(
          'flex w-full items-center gap-3 px-4 py-3 text-left',
          hasDetails && 'cursor-pointer',
        )}
      >
        <div
          className={clsx(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
            isDone ? 'bg-success text-white' : 'bg-surface-2 text-text-secondary',
          )}
        >
          {isDone ? <Check className="h-4 w-4" /> : step.id}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-text-tertiary" />
            <p
              className={clsx(
                'text-sm font-medium',
                isDone ? 'text-success line-through' : 'text-text-primary',
              )}
            >
              {step.title}
            </p>
          </div>
          {!isExpanded && (
            <p className="mt-0.5 truncate pl-6 text-xs text-text-tertiary">{step.description}</p>
          )}
        </div>

        {hasDetails && (
          <div className="shrink-0 text-text-tertiary">
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        )}
      </button>

      {isExpanded && hasDetails && (
        <div className="border-t border-surface-3 px-4 pb-4 pt-3">
          <p className="text-sm text-text-secondary">{step.description}</p>

          {step.instructions.length > 0 && (
            <ol className="mt-3 space-y-2 pl-4">
              {step.instructions.map((instruction) => (
                <li
                  key={instruction}
                  className="list-decimal pl-1 text-sm text-text-primary"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: intentional for rendered HTML content
                  dangerouslySetInnerHTML={{ __html: instruction }}
                />
              ))}
            </ol>
          )}

          {step.tip && (
            <div className="mt-3 rounded-lg bg-brand-50 px-3 py-2">
              <p className="text-xs text-brand-700">
                <span className="font-semibold">Tip:</span> {step.tip}
              </p>
            </div>
          )}

          {/* Inline interactive content for self-contained steps */}
          {renderContent && <div className="mt-4">{renderContent}</div>}

          {/* Step 5: Manual shortcode confirmation checkbox */}
          {step.manual && (
            <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-lg border border-surface-3 bg-surface-1 px-4 py-3">
              <input
                type="checkbox"
                checked={shortcodeConfirmed}
                onChange={onShortcodeToggle}
                className="h-4 w-4 rounded border-surface-3 text-brand-500 focus:ring-brand-500"
              />
              <span className="text-sm text-text-primary">
                I&apos;ve connected my subscribe form
              </span>
            </label>
          )}

          {/* Step 6: Test connection */}
          {step.id === 6 && (
            <div className="mt-4">
              <TestConnection
                audienceId={audienceId}
                hasAudience={hasAudience}
                onTestPassed={onTestPassed}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
